import React, { FC, useState, Fragment } from 'react';
import {
    List,
    Datagrid,
    TextField,
    NumberField,
    EditButton,
    useNotify,
    useDelete,
    ListProps,
    Button as RAButton,
    useRecordContext,
    TextInput,
    useGetIdentity
} from 'react-admin';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ICityShipping } from '../types/types';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';

const DeleteWithConfirmButton: FC = () => {
    const { identity } = useGetIdentity();
    const userRole = identity?.role_id;
    const record = useRecordContext<ICityShipping>();
    const [deleteOne, { isLoading }] = useDelete();
    const [open, setOpen] = useState(false);
    const notify = useNotify();

    // Only render for Admin users
    if (userRole !== UserRole.Admin) {
        return null;
    }

    const handleDelete = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const confirmDelete = async () => {
        await deleteOne('cityshipping', { id: record.id });
        notify('resources.cityshipping.notification.deleted', {
            type: 'info',
            messageArgs: { smart_count: 1, name: 'City Shipping' }
        });
        setOpen(false);
    };

    return (
        <Fragment>
            <RAButton
                label="Delete"
                onClick={handleDelete}
                startIcon={<DeleteIcon />}
                disabled={isLoading}
            />
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this city shipping record?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

const ShippingFilter = [
    <TextInput label="From City" source="city_from" resettable alwaysOn/>,
    <TextInput label="To City" source="city_to" resettable alwaysOn/>,
];

export const CityShippingList: FC<ListProps> = (props) => {
    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);
    return (
        <List 
            filters={ShippingFilter} 
            {...props}
            sort={{ field: 'city_from_name', order: 'ASC' }}
            perPage={25}
        >
            <Datagrid bulkActionButtons={false}>
                <TextField source="city_from_name" label="From City" />
                <TextField source="city_to_name" label="To City" />
                <NumberField 
                    source="shipping_charge" 
                    options={{ style: 'currency', currency: 'INR' }} 
                    label="Shipping Charge"
                />
                <EditButton />
                <DeleteWithConfirmButton />
            </Datagrid>
        </List>
    );
};